/** The painted water boundary, shared by swimming wildlife and the scene. World units. */
export interface WaterHabitat { cx: number; cy: number; rx: number; ry: number }
export interface WaterPoint { x: number; y: number }

export const rand = (min: number, max: number): number => min + Math.random() * (max - min);

/** A resumed tab or invalid clock value must not fling wildlife across the pond. */
export function boundedDelta(seconds: number): number {
  return Number.isFinite(seconds) ? Math.max(0, Math.min(seconds, 0.1)) : 0;
}

/** Uniform area sampling, leaving enough room for the animal's body at the bank. */
export function waterPoint(h: WaterHabitat, inset = 45, extent = 0.86): WaterPoint {
  const angle = rand(0, Math.PI * 2), radius = Math.sqrt(Math.random()) * extent;
  return { x: h.cx + Math.cos(angle) * Math.max(1, h.rx - inset) * radius, y: h.cy + Math.sin(angle) * Math.max(1, h.ry - inset) * radius };
}

export function waterRadius(p: WaterPoint, h: WaterHabitat, inset = 0): number {
  return Math.hypot((p.x - h.cx) / Math.max(1, h.rx - inset), (p.y - h.cy) / Math.max(1, h.ry - inset));
}

/** A hard last-resort boundary; normal travel starts turning well before this is needed. */
export function keepInWater(p: WaterPoint, h: WaterHabitat, inset = 45): void {
  const radius = waterRadius(p, h, inset);
  if (radius > 1) {
    p.x = h.cx + (p.x - h.cx) / radius;
    p.y = h.cy + (p.y - h.cy) / radius;
  }
}

/** Shortest-arc turns avoid a spin when headings cross -PI/PI. */
export function turnToward(current: number, target: number, maxTurn: number): number {
  const delta = Math.atan2(Math.sin(target - current), Math.cos(target - current));
  return current + Math.max(-maxTurn, Math.min(maxTurn, delta));
}
